import React from 'react';

const BurndownChart = ({ sprints, issues, selectedProject }) => {
  // This is a simplified implementation - in a real app, you'd use a charting library like Chart.js
  const activeSprint = sprints.find(sprint => sprint.status === 'active');
  
  if (!activeSprint) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Burndown Chart
        </h3>
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">📊</div>
          <p className="text-gray-500 dark:text-gray-400">
            No active sprint to display burndown chart
          </p>
        </div>
      </div>
    );
  }

  const sprintIssues = issues.filter(issue => 
    issue.sprintId === activeSprint.id &&
    (selectedProject === 'all' || issue.projectId === selectedProject)
  );

  const totalStoryPoints = sprintIssues.reduce((sum, issue) => sum + (parseInt(issue.storyPoints) || 0), 0);
  const completedStoryPoints = sprintIssues
    .filter(issue => issue.status === 'done')
    .reduce((sum, issue) => sum + (parseInt(issue.storyPoints) || 0), 0);

  const startDate = new Date(activeSprint.startDate);
  const endDate = new Date(activeSprint.endDate);
  const today = new Date();
  
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  const daysPassed = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, totalDays - daysPassed);

  const idealBurnRate = totalStoryPoints / totalDays;
  const idealRemaining = Math.max(0, totalStoryPoints - (idealBurnRate * daysPassed));
  const actualRemaining = Math.max(0, totalStoryPoints - completedStoryPoints);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Burndown Chart - {activeSprint.name}
      </h3>
      
      <div className="space-y-4">
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>Start: {startDate.toLocaleDateString()}</span>
          <span>End: {endDate.toLocaleDateString()}</span>
          <span>Today: {today.toLocaleDateString()}</span>
        </div>

        {/* Simplified chart visualization */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="flex items-end justify-between h-40">
            {/* Ideal burn line */}
            <div className="flex-1 relative">
              <div className="absolute top-0 left-0 right-0 border-t-2 border-dashed border-blue-400"></div>
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Ideal</div>
            </div>
            
            {/* Actual burn line */}
            <div className="flex-1 relative">
              <div 
                className="absolute top-0 left-0 right-0 border-t-2 border-green-500"
                style={{ top: `${(actualRemaining / totalStoryPoints) * 100}%` }}
              ></div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-1">Actual</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {totalStoryPoints}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Points</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {completedStoryPoints}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {actualRemaining}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Remaining</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {daysRemaining}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Days Left</div>
          </div>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p>
            <span className="font-semibold">Progress:</span>{' '}
            {((completedStoryPoints / totalStoryPoints) * 100).toFixed(1)}% complete
          </p>
          <p>
            <span className="font-semibold">Required daily rate:</span>{' '}
            {(actualRemaining / Math.max(1, daysRemaining)).toFixed(1)} points/day
          </p>
        </div>
      </div>
    </div>
  );
};

export default BurndownChart;